/**
 * Sign in and register.
 *
 * One form with a mode switch rather than two routes. Registration and sign-in
 * differ by one field, and splitting them means a user who mistypes their password
 * has to work out whether they are on the wrong page or have the wrong password.
 *
 * ## What is deliberately absent
 *
 * - **No "forgot password".** Password reset needs transactional email, a
 *   single-use token with its own expiry, and a rate limit per address. None of
 *   those exists here, and a link that goes nowhere is worse than no link. It is
 *   listed as a known gap in docs/SECURITY.md.
 * - **No OAuth.** Same reason: it is a whole identity-provider integration, and
 *   this project is about the realtime layer.
 * - **No password strength meter.** See the note on `passwordSchema` in
 *   `packages/shared/src/http.ts`: composition rules reduce entropy, and the real
 *   defence is the PBKDF2 work factor. The field says "at least 12 characters"
 *   and that is the whole policy.
 *
 * ## Error handling
 *
 * A 401 from `/login` returns one message for "no such account" and "wrong
 * password", because the server does not distinguish them - see `invalidCredentials`
 * in `apps/api/src/errors.ts`. The client must not invent a distinction the server
 * deliberately withholds, so the message is rendered verbatim.
 *
 * Per-field validation errors from a 400 are attached to the inputs they belong
 * to, using the `errors[].path` the server returns. That is what the RFC 9457
 * extension members are for.
 */

import { cloneElement, isValidElement, useState, type FormEvent, type ReactElement, type ReactNode } from 'react';
import { useAuth } from '../state/AuthProvider.js';
import { ApiError } from '../api/client.js';
import { Spinner, inputClass, primaryButtonClass } from './ui.js';

type Mode = 'login' | 'register';

/**
 * The password every seeded account shares.
 *
 * Committed deliberately. This is a demonstration deployment whose seed is in the
 * repository, so the credentials are already public; refusing to name them here
 * would not protect anything and would make the demo harder to evaluate.
 */
const DEMO_PASSWORD = 'slate-demo-password';

export function SignIn(): ReactNode {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});

    try {
      if (mode === 'register') await register({ email, name, password });
      else await login({ email, password });
    } catch (caught) {
      if (caught instanceof ApiError) {
        const byField: Record<string, string> = {};
        for (const issue of caught.errors) {
          // The server's paths are prefixed with the location they were parsed
          // from (`body.email`, `query.limit`). Stripping the prefix lets the
          // field name be used directly as a key.
          byField[issue.path.replace(/^body\./, '')] = issue.message;
        }
        setFieldErrors(byField);
        // Only surface a banner when the failure is already attached to a
        // field. Showing both is how a form ends up saying the same thing twice.
        if (Object.keys(byField).length === 0) setError(caught.detail);
      } else {
        setError('The request could not be completed. Check your connection and try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  /**
   * Sign in as a seeded account without typing anything.
   *
   * A demo that requires a visitor to read a credential off the screen, type it
   * correctly, and then find their way to a board is a demo most visitors never
   * see. This exists so the first click lands on a populated board.
   *
   * The credentials are the seed's, and they are public on purpose — this is a
   * demonstration deployment with a published seed, so hiding them would make the
   * project harder to evaluate without making it more secure.
   */
  async function signInAsDemo(who: 'rahil' | 'mira'): Promise<void> {
    setBusy(true);
    setError(null);
    setFieldErrors({});
    setEmail(`${who}@slate.dev`);
    setPassword(DEMO_PASSWORD);
    try {
      await login({ email: `${who}@slate.dev`, password: DEMO_PASSWORD });
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.detail
          : 'The demo account could not be reached. The seed may not have been applied.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-full items-center justify-center bg-canvas px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Slate</h1>
          <p className="mt-1 text-sm text-ink-muted">
            A real-time collaborative board. Open a card in two windows and watch it move.
          </p>
        </div>

        <div className="rounded-xl border border-border bg-surface p-5 shadow-sm">
          <div role="tablist" aria-label="Sign in or register" className="mb-4 flex gap-1 rounded-lg bg-sunken p-1">
            {(['login', 'register'] as const).map((candidate) => (
              <button
                key={candidate}
                type="button"
                role="tab"
                aria-selected={mode === candidate}
                onClick={() => {
                  setMode(candidate);
                  setError(null);
                  setFieldErrors({});
                }}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  mode === candidate ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
                }`}
              >
                {candidate === 'login' ? 'Sign in' : 'Create account'}
              </button>
            ))}
          </div>

          <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-3" noValidate>
            {mode === 'register' && (
              <Field label="Name" htmlFor="name" error={fieldErrors['name']}>
                <input
                  id="name"
                  name="name"
                  autoComplete="name"
                  required
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className={inputClass}
                />
              </Field>
            )}

            <Field label="Email" htmlFor="email" error={fieldErrors['email']}>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className={inputClass}
              />
            </Field>

            <Field
              label="Password"
              htmlFor="password"
              error={fieldErrors['password']}
              hint={mode === 'register' ? 'At least 12 characters. No other rules.' : undefined}
            >
              <input
                id="password"
                name="password"
                type="password"
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className={inputClass}
              />
            </Field>

            {error !== null && (
              <p role="alert" className="rounded-md bg-danger-soft px-2.5 py-2 text-xs text-danger">
                {error}
              </p>
            )}

            <button type="submit" disabled={busy} className={`${primaryButtonClass} mt-1 w-full`}>
              {busy ? <Spinner label="Signing in" /> : null}
              {mode === 'login' ? 'Sign in' : 'Create account'}
            </button>
          </form>
        </div>

        {/*
          * The demo entry point.
          *
          * Two buttons rather than one, because the point of this project is
          * collaboration: a single signed-in session shows a board, and two
          * sessions in two browser profiles show cursors, presence and live
          * updates. Naming both accounts here is what makes that discoverable.
          */}
        <div className="mt-4 rounded-xl border border-border bg-surface p-4">
          <p className="text-xs font-medium text-ink">Try it without an account</p>
          <p className="mt-1 text-xs leading-relaxed text-ink-subtle">
            Both buttons sign in to a seeded workspace with boards, columns and cards
            already in place. Open the second account in a different browser profile to
            see live cursors and presence.
          </p>

          <div className="mt-3 flex flex-col gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void signInAsDemo('rahil')}
              className={`${primaryButtonClass} w-full`}
            >
              {busy ? <Spinner label="Signing in" /> : null}
              Open the demo as Rahil (owner)
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void signInAsDemo('mira')}
              className="w-full rounded-md border border-border bg-canvas px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-sunken disabled:opacity-50"
            >
              Open as Mira (admin) — for a second window
            </button>
          </div>

          <p className="mt-3 text-center text-[11px] text-ink-subtle">
            <span className="font-mono">rahil@slate.dev</span>,{' '}
            <span className="font-mono">mira@slate.dev</span>,{' '}
            <span className="font-mono">dev@slate.dev</span> — all with{' '}
            <span className="font-mono">slate-demo-password</span>
          </p>
        </div>
      </div>
    </main>
  );
}

function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}): ReactNode {
  const describedBy = [error === undefined ? null : `${htmlFor}-error`, hint === undefined ? null : `${htmlFor}-hint`]
    .filter((value): value is string => value !== null)
    .join(' ');

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="text-xs font-medium text-ink-muted">
        {label}
      </label>
      {/* The inputs are rendered by the caller; `aria-describedby` is applied by
          cloning so the Field does not have to own the input's other props. */}
      {cloneWithDescription(children, describedBy)}
      {hint !== undefined && (
        <p id={`${htmlFor}-hint`} className="text-[11px] text-ink-subtle">
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p id={`${htmlFor}-error`} className="text-[11px] text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/** Attach `aria-describedby` to a single child element.
 *
 * A tiny helper rather than passing the attribute through props, because the
 * alternative is threading `aria-describedby` through every call site - and a
 * call site that forgets it produces a validation message that a screen reader
 * user cannot associate with the field it belongs to.
 *
 * `cloneElement` rather than spreading props at the call site: it preserves the
 * child's own props and its key, so a field can be given any number of extra
 * attributes without the `Field` wrapper having to know about them. */
function cloneWithDescription(child: ReactNode, describedBy: string): ReactNode {
  if (describedBy === '') return child;
  if (!isValidElement(child)) return child;
  return cloneElement(child as ReactElement<{ 'aria-describedby'?: string }>, { 'aria-describedby': describedBy });
}
